import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, Globe, Database, FileText, Mail, Users, Scale, UserCheck, AlertTriangle } from 'lucide-react';
import Footer from "./Footer";
import Navbar from "./Navbar";
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [visibleSections, setVisibleSections] = useState([]);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    // Animate hero section on mount
    const heroTimer = setTimeout(() => setHeroVisible(true), 200);
    
    // Animate sections with staggered delay
    const sectionTimers = sections.map((_, index) => 
      setTimeout(() => {
        setVisibleSections(prev => [...prev, index]);
      }, 800 + (index * 300))
    );

    return () => {
      clearTimeout(heroTimer);
      sectionTimers.forEach(clearTimeout);
    };
  }, []);

  const sections = [
    {
      id: 'data-controller',
      title: 'Data Controller Information',
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Acting under Article 13 paragraphs 1 and 2 of the General Data Protection Regulation (GDPR), we inform you about our data processing practices.
          </p>
          <div className="bg-blue-50 p-6 rounded-xl transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
            <h4 className="font-semibold text-blue-900 mb-3">Contact Information</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <p className="transform hover:translate-x-2 transition-transform duration-200">• Company: Ziegler</p>
              <p className="transform hover:translate-x-2 transition-transform duration-200">• Email: privacy@ziegler.com</p>
              <p className="transform hover:translate-x-2 transition-transform duration-200">• Data Protection Officer: dpo@ziegler.com</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'processing-purposes',
      title: 'Purposes of Data Processing',
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Personal data will be processed for the purpose of providing services by Ziegler to our clients and partners.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 p-6 rounded-xl transform hover:-rotate-1 hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <h4 className="font-semibold text-green-900 mb-3">Service Provision</h4>
              <ul className="space-y-2 text-sm text-green-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Implementation of electronic services</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Processing inquiries and requests</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Customer support and communication</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Newsletter services (with consent)</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl transform hover:rotate-1 hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <h4 className="font-semibold text-purple-900 mb-3">Business Operations</h4>
              <ul className="space-y-2 text-sm text-purple-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Website content personalization</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Service improvement measurements</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Marketing and promotion activities</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Legal claims and defense</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'legal-basis',
      title: 'Legal Basis for Processing',
      icon: <Scale className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            The basis for processing personal data is Article 6, paragraph 1 of the GDPR, specifically:
          </p>
          <div className="bg-indigo-50 p-6 rounded-xl hover:bg-indigo-100 transition-colors duration-300 hover:shadow-lg">
            <h4 className="font-semibold text-indigo-900 mb-3">Legal Grounds</h4>
            <div className="space-y-3 text-sm text-indigo-800">
              <div className="flex items-start space-x-3">
                <span className="bg-indigo-200 text-indigo-900 px-2 py-1 rounded text-xs font-semibold">Art. 6(1)(b)</span>
                <p>Performance of contract or pre-contractual measures</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="bg-indigo-200 text-indigo-900 px-2 py-1 rounded text-xs font-semibold">Art. 6(1)(c)</span>
                <p>Compliance with legal obligations</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="bg-indigo-200 text-indigo-900 px-2 py-1 rounded text-xs font-semibold">Art. 6(1)(f)</span>
                <p>Legitimate interests pursued by Ziegler</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'data-recipients',
      title: 'Data Recipients',
      icon: <Globe className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Personal data may be shared with the following categories of recipients:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg text-center group hover:bg-orange-100 transition-all duration-300 hover:shadow-lg transform hover:scale-105">
              <Users className="w-8 h-8 mx-auto text-orange-500 mb-2 group-hover:animate-bounce" />
              <h5 className="font-semibold text-orange-900">Internal Recipients</h5>
              <p className="text-sm text-orange-700">Ziegler and its authorized representatives</p>
            </div>
            <div className="bg-teal-50 p-4 rounded-lg text-center group hover:bg-teal-100 transition-all duration-300 hover:shadow-lg transform hover:scale-105">
              <Globe className="w-8 h-8 mx-auto text-teal-500 mb-2 group-hover:animate-bounce" />
              <h5 className="font-semibold text-teal-900">External Partners</h5>
              <p className="text-sm text-teal-700">Service providers, IT services, legal services</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center group hover:bg-red-100 transition-all duration-300 hover:shadow-lg transform hover:scale-105">
              <Shield className="w-8 h-8 mx-auto text-red-500 mb-2 group-hover:animate-bounce" />
              <h5 className="font-semibold text-red-900">Authorities</h5>
              <p className="text-sm text-red-700">State authorities when legally required</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'retention-period',
      title: 'Data Retention',
      icon: <Database className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Personal data will be stored for the period necessary under legal provisions and agreements.
          </p>
          <div className="bg-gray-50 p-6 rounded-xl hover:bg-gray-100 transition-colors duration-300">
            <h4 className="font-semibold text-gray-900 mb-3">Retention Criteria</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="transform hover:translate-x-2 transition-transform duration-200">• Duration necessary for contract execution</li>
              <li className="transform hover:translate-x-2 transition-transform duration-200">• Period required by applicable law</li>
              <li className="transform hover:translate-x-2 transition-transform duration-200">• Time needed for pursuing or defending claims</li>
              <li className="transform hover:translate-x-2 transition-transform duration-200">• As long as legitimate business interests exist</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'data-rights',
      title: 'Your Rights',
      icon: <UserCheck className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Under GDPR, you have the following rights regarding your personal data:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg hover:bg-blue-100 transition-colors duration-300 hover:shadow-lg">
              <h5 className="font-semibold text-blue-900 mb-2">Access & Control Rights</h5>
              <ul className="space-y-1 text-sm text-blue-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to access your data</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to rectification</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to erasure</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to restrict processing</li>
              </ul>
            </div>
            <div className="bg-green-50 p-4 rounded-lg hover:bg-green-100 transition-colors duration-300 hover:shadow-lg">
              <h5 className="font-semibold text-green-900 mb-2">Additional Rights</h5>
              <ul className="space-y-1 text-sm text-green-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to data portability</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to object</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to withdraw consent</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Right to lodge a complaint</li>
              </ul>
            </div>
          </div>
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg hover:bg-amber-100 transition-colors duration-300">
            <p className="text-amber-800 font-medium">
              <AlertTriangle className="w-5 h-5 inline mr-2" />
              You can exercise these rights by contacting our Data Protection Officer at dpo@ziegler.com
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'complaints',
      title: 'Supervisory Authority',
      icon: <Scale className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            You have the right to lodge a complaint with the supervisory authority if you believe your data is being processed unlawfully.
          </p>
          <div className="bg-red-50 p-6 rounded-xl hover:bg-red-100 transition-colors duration-300 hover:shadow-lg">
            <h4 className="font-semibold text-red-900 mb-3">Filing a Complaint</h4>
            <p className="text-sm text-red-800 mb-3">
              Contact your local Data Protection Authority if you believe our processing violates GDPR requirements.
            </p>
            <p className="text-xs text-red-600">
              This right exists independently of any other administrative or judicial remedy.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'data-provision',
      title: 'Data Provision & Processing',
      icon: <Database className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Information about the voluntary nature of data provision and automated processing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-yellow-50 p-6 rounded-xl transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <h4 className="font-semibold text-yellow-900 mb-3">Data Provision</h4>
              <p className="text-sm text-yellow-800 mb-2">
                Providing personal data is voluntary but necessary for:
              </p>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Establishing cooperation</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Service delivery</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Contract fulfillment</li>
              </ul>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <h4 className="font-semibold text-indigo-900 mb-3">Automated Processing</h4>
              <p className="text-sm text-indigo-800 mb-2">
                Data processing includes both:
              </p>
              <ul className="space-y-1 text-sm text-indigo-800">
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Automated methods</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• Non-automated methods</li>
                <li className="transform hover:translate-x-2 transition-transform duration-200">• No automated decision-making</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: <Globe className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p className="text-white leading-relaxed">
            Information about data storage and transfers outside the European Economic Area.
          </p>
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg hover:bg-orange-100 transition-colors duration-300">
            <h4 className="font-semibold text-orange-900 mb-2">Data Storage</h4>
            <p className="text-orange-800 text-sm mb-2">
              Ziegler stores data in IT infrastructure provided by external entities. Some servers may be located outside the European Economic Area.
            </p>
            <p className="text-orange-700 text-xs">
              <Globe className="w-4 h-4 inline mr-1" />
              All transfers comply with applicable data protection laws and regulations.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
    <Navbar/>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className={`text-white py-16 transition-all duration-1000 ${heroVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-10'}`} style={{backgroundColor: '#43b6c6'}}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <Shield className="w-16 h-16 mx-auto mb-6 text-blue-200 animate-pulse hover:animate-spin transition-all duration-500" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in">Privacy Policy</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed opacity-0 animate-fade-in-delay">
              Information on the processing of personal data in accordance with GDPR.
              We are committed to protecting your privacy and personal data.
            </p>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className={`bg-white rounded-2xl shadow-lg p-8 mb-12 transition-all duration-1000 hover:shadow-2xl transform hover:scale-105 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex items-start space-x-4">
            <Mail className="w-8 h-8 text-blue-500 mt-1 hover:animate-bounce transition-all duration-300" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">GDPR Compliance Notice</h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-4">
                Acting under Article 13 paragraphs 1 and 2 of Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016 on the protection of natural persons with regard to the processing of personal data and on the free movement of such data (General Data Protection Regulation), we hereby inform you about our data processing practices.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <Shield className="w-4 h-4 inline mr-2" />
                  This policy outlines how Ziegler collects, processes, and protects your personal data in compliance with GDPR requirements.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div 
              key={section.id} 
              className={`rounded-2xl shadow-lg overflow-hidden transform transition-all duration-1000 hover:shadow-2xl hover:scale-105 ${
                visibleSections.includes(index) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-20'
              }`} 
              style={{backgroundColor: '#43b6c6'}}
            >
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-8 py-6 border-b hover:from-blue-50 hover:to-gray-50 transition-all duration-500">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 hover:rotate-12 transition-all duration-300 hover:shadow-lg">
                    {section.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 hover:text-blue-700 transition-colors duration-300">{section.title}</h2>
                    <p className="text-sm text-gray-500">Section {index + 1}</p>
                  </div>
                </div>
              </div>
              <div className="p-8">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className={`text-white rounded-2xl p-8 mt-12 transform transition-all duration-1000 hover:shadow-2xl hover:scale-105 ${
          visibleSections.length >= sections.length ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`} style={{backgroundColor: '#43b6c6'}}>
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 text-blue-200 hover:animate-bounce transition-all duration-300" />
            <h3 className="text-2xl font-bold mb-4">Questions About Data Processing?</h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              If you have any questions about our data processing practices or wish to exercise your rights under GDPR, please contact our Data Protection Officer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-110 hover:shadow-lg">
                <Link to="/contactus">
                Contact Us
                </Link>
              </button>
              <button className="bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-all duration-300 transform hover:scale-110 hover:shadow-lg">
                Email DPO: dpo@ziegler.com
              </button>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className={`text-center mt-8 transition-all duration-1000 ${
          visibleSections.length >= sections.length ? 'opacity-100' : 'opacity-0'
        }`}>
          <p className="text-gray-500 text-sm hover:text-gray-700 transition-colors duration-300">
            Last updated: June 27, 2025 | Compliant with GDPR (EU) 2016/679
          </p>
        </div>
      </div>
    </div>
    <Footer/>

    <style jsx>{`
      @keyframes fade-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .animate-fade-in {
        animation: fade-in 1s ease-out forwards;
      }
      
      .animate-fade-in-delay {
        animation: fade-in 1s ease-out 0.5s forwards;
      }
    `}</style>
    </>
  );
};

export default PrivacyPolicy;